import { useState, useCallback, useMemo } from 'react'
import { Box, Button, Typography } from '@mui/material'
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet'
import ConfirmModal from './ConfirmModal'
import ErrorModal from './ErrorModal'
import { useTheme } from '@/contexts/ThemeContext'
import { ConnectionStatus } from '@/constants/connectionStatus'
import { useWallet } from '@/hooks/useWallet'

export default function Header() {
  const { status, address, connectWallet, disconnectWallet, formatAddress } =
    useWallet()
  const [showDisconnectModal, setShowDisconnectModal] = useState(false)
  const [errorModal, setErrorModal] = useState<{
    open: boolean
    title: string
    message: string
  }>({
    open: false,
    title: '',
    message: '',
  })
  const { colors } = useTheme()

  const handleConnectClick = useCallback(async () => {
    if (status === ConnectionStatus.Connected) {
      setShowDisconnectModal(true)
      return
    }
    await connectWallet()
  }, [status, connectWallet])

  const handleDisconnect = useCallback(() => {
    disconnectWallet()
    setShowDisconnectModal(false)
  }, [disconnectWallet])

  const handleCloseModal = useCallback(() => {
    setShowDisconnectModal(false)
  }, [])

  const buttonText = useMemo(() => {
    switch (status) {
      case ConnectionStatus.Connecting:
        return 'Connecting...'
      case ConnectionStatus.Connected:
        return formatAddress(address)
      default:
        return 'Connect Wallet'
    }
  }, [status, address, formatAddress])

  const buttonColor = useMemo(() => {
    switch (status) {
      case ConnectionStatus.Connecting:
        return colors.warning
      case ConnectionStatus.Connected:
        return colors.primary
      default:
        return colors.secondary
    }
  }, [status, colors])

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 4,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          color: colors.primary,
          textShadow: `0 0 10px ${colors.primaryShadow}, 0 0 10px ${colors.primaryShadow}, 0 0 10px ${colors.primaryShadow}`,
          fontSize: { xs: '25px', sm: '30px', md: '50px' },
        }}
      >
        Admin Dashboard
      </Typography>

      <Button
        variant="contained"
        onClick={handleConnectClick}
        disabled={status === ConnectionStatus.Connecting}
        startIcon={
          <AccountBalanceWalletIcon
            sx={{
              display: { xs: 'block', sm: 'none' },
              width: '20px',
              height: '20px',
            }}
          />
        }
        sx={{
          backgroundColor: buttonColor,
          color: colors.background,
          fontWeight: 700,
          fontSize: '16px',
          px: 4,
          py: 1.5,
          textTransform: 'uppercase',
          '&:hover': {
            backgroundColor: buttonColor,
            opacity: 0.9,
          },
          '&.Mui-disabled': {
            backgroundColor: colors.warning,
            color: colors.background,
          },
          '& .MuiButton-startIcon': {
            margin: 0,
          },
          '& .MuiButton-text': {
            display: { xs: 'none', sm: 'block' },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: { xs: 'none', sm: 'inline' },
          }}
        >
          {buttonText}
        </Box>
      </Button>

      <ConfirmModal
        open={showDisconnectModal}
        title="Disconnect Wallet"
        message="Are you sure you want to disconnect your wallet?"
        onConfirm={handleDisconnect}
        onCancel={handleCloseModal}
        confirmText="Yes"
        cancelText="No"
      />

      <ErrorModal
        open={errorModal.open}
        title={errorModal.title}
        message={errorModal.message}
        onClose={() => setErrorModal({ open: false, title: '', message: '' })}
      />
    </Box>
  )
}
